const BASE = '/api';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const data = await res.json();
  if (!res.ok) throw new Error((data as any).error || 'Request failed');
  return data as T;
}

export type Role = 'platform_admin' | 'ceo' | 'director' | 'head_of_dept' | 'finance' | 'employee';

export interface User {
  id: string;
  business_id: string | null;
  email: string;
  full_name: string | null;
  role: Role;
  department_id: string | null;
  directorate_id: string | null;
  is_active: number;
  email_verified: number;
  is_finance_approver?: boolean;
  created_at: number;
  updated_at: number;
}

export interface Business {
  id: string;
  name: string;
  currency: string;
  fiscal_year_start_month: number;
  is_active: number;
  acting_ceo_user_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface FinanceApprover {
  id: string;
  business_id: string;
  user_id: string;
  full_name: string | null;
  email: string;
  role: Role;
  created_at: number;
}

export interface Directorate {
  id: string;
  business_id: string;
  name: string;
  director_user_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface Department {
  id: string;
  business_id: string;
  directorate_id: string;
  name: string;
  head_user_id: string | null;
  approval_workflow_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface Budget {
  id: string;
  department_id: string;
  department_name?: string;
  fiscal_year: number;
  amount_allocated: number;
  amount_spent: number;
  created_at: number;
  updated_at: number;
}

export type ExpenseStatus =
  | 'pending_approver' | 'rejected_by_approver' | 'pending_finance'
  | 'rejected_by_finance' | 'approved' | 'paid';

export interface Expense {
  id: string;
  business_id: string;
  submitter_user_id: string;
  department_id: string;
  approver_user_id: string | null;
  amount: number;
  description: string | null;
  expense_date: string;
  receipt_r2_key: string | null;
  fiscal_year: number;
  status: ExpenseStatus;
  approver_decision_at: number | null;
  approver_comment: string | null;
  finance_user_id: string | null;
  finance_decision_at: number | null;
  finance_comment: string | null;
  paid_at: number | null;
  purchase_request_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface ExpenseEvent {
  id: string;
  expense_id: string;
  event_type: string;
  actor_user_id: string | null;
  comment: string | null;
  created_at: number;
}

export type StepType = 'line_manager' | 'specific_user';
export type PurchaseRequestStatus = 'pending_approval' | 'approved' | 'rejected' | 'cancelled';
export type PurchaseRequestStepStatus = 'pending' | 'current' | 'approved' | 'rejected' | 'skipped';

export interface Workflow {
  id: string;
  business_id: string;
  name: string;
  is_default: number;
  created_at: number;
  updated_at: number;
}

export interface WorkflowStep {
  id: string;
  workflow_id: string;
  step_order: number;
  step_type: StepType;
  specific_user_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface PurchaseRequest {
  id: string;
  business_id: string;
  submitter_user_id: string;
  department_id: string;
  workflow_id: string;
  item_description: string;
  estimated_amount: number;
  justification: string | null;
  needed_by_date: string | null;
  status: PurchaseRequestStatus;
  current_step_order: number | null;
  final_decision_at: number | null;
  final_comment: string | null;
  created_at: number;
  updated_at: number;
}

export interface PurchaseRequestStep {
  id: string;
  purchase_request_id: string;
  step_order: number;
  step_type: StepType;
  approver_user_id: string | null;
  status: PurchaseRequestStepStatus;
  decision_at: number | null;
  comment: string | null;
  created_at: number;
  updated_at: number;
}

export interface PurchaseRequestEvent {
  id: string;
  purchase_request_id: string;
  purchase_request_step_id: string | null;
  event_type: string;
  actor_user_id: string | null;
  comment: string | null;
  created_at: number;
}

export interface DepartmentReportRow {
  department_id: string;
  department_name: string;
  directorate_name: string;
  head_user_id: string | null;
  head_name: string | null;
  allocated: number;
  spent: number;
}

export interface ExpenseReportRow extends Expense {
  submitter_name: string;
}

export interface DirectorateReportRow {
  directorate_id: string;
  directorate_name: string;
  allocated: number;
  spent: number;
}

// Auth
export const auth = {
  setupStatus: () => request<{ needsBootstrap: boolean }>('GET', '/auth/setup-status'),
  bootstrapAdmin: (email: string, password: string, full_name?: string) =>
    request<{ message: string }>('POST', '/auth/bootstrap-admin', { email, password, full_name }),
  login: (email: string, password: string) =>
    request<{ user: User }>('POST', '/auth/login', { email, password }),
  logout: () => request<{ message: string }>('POST', '/auth/logout'),
  forgotPassword: (email: string) =>
    request<{ message: string }>('POST', '/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    request<{ message: string }>('POST', '/auth/reset-password', { token, password }),
  me: () => request<{ user: User; impersonatedBy: ImpersonatedBy | null }>('GET', '/auth/me'),
  stopImpersonating: () => request<{ user: User }>('POST', '/auth/stop-impersonating'),
};

export interface ImpersonatedBy {
  id: string;
  full_name: string | null;
  email: string;
}

// Profile (self-service)
export const profile = {
  update: (full_name: string) => request<{ user: User }>('PATCH', '/profile', { full_name }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ message: string }>('PATCH', '/profile/password', { currentPassword, newPassword }),
};

// Admin: businesses (platform_admin only)
export const adminBusinesses = {
  list: () => request<{ businesses: Business[] }>('GET', '/admin/businesses'),
  create: (data: { name: string; currency?: string; fiscal_year_start_month?: number; ceo_email: string; ceo_full_name?: string }) =>
    request<{ business: Business; ceo: User }>('POST', '/admin/businesses', data),
  get: (id: string) => request<{ business: Business; userCount: number }>('GET', `/admin/businesses/${id}`),
  update: (id: string, data: Partial<Business>) =>
    request<{ business: Business }>('PUT', `/admin/businesses/${id}`, data),
  remove: (id: string) => request<{ message: string }>('DELETE', `/admin/businesses/${id}`),
};

// Admin: users (platform_admin: any business, ceo: own business)
export const adminUsers = {
  list: (search?: string, businessId?: string) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (businessId) params.set('business_id', businessId);
    const qs = params.toString();
    return request<{ users: User[] }>('GET', `/admin/users${qs ? `?${qs}` : ''}`);
  },
  create: (data: { email: string; full_name?: string; role: Role; department_id?: string; directorate_id?: string; business_id?: string }) =>
    request<{ user: User }>('POST', '/admin/users', data),
  get: (id: string) => request<{ user: User }>('GET', `/admin/users/${id}`),
  update: (id: string, data: Partial<User>) =>
    request<{ user: User }>('PUT', `/admin/users/${id}`, data),
  remove: (id: string) => request<{ message: string }>('DELETE', `/admin/users/${id}`),
  resetPassword: (id: string, password: string) =>
    request<{ message: string }>('PATCH', `/admin/users/${id}/password`, { password }),
  resendInvite: (id: string) => request<{ message: string }>('POST', `/admin/users/${id}/resend-invite`),
  impersonate: (id: string) => request<{ user: User }>('POST', `/admin/users/${id}/impersonate`),
};

function bizQuery(businessId?: string) {
  return businessId ? `?business_id=${businessId}` : '';
}

// Business setup: directorates / departments / budgets
export const business = {
  directorates: {
    list: (businessId?: string) => request<{ directorates: Directorate[] }>('GET', `/business/directorates${bizQuery(businessId)}`),
    create: (name: string, businessId?: string) =>
      request<{ directorate: Directorate }>('POST', `/business/directorates${bizQuery(businessId)}`, { name }),
    update: (id: string, data: Partial<Directorate>, businessId?: string) =>
      request<{ directorate: Directorate }>('PUT', `/business/directorates/${id}${bizQuery(businessId)}`, data),
    remove: (id: string, businessId?: string) =>
      request<{ message: string }>('DELETE', `/business/directorates/${id}${bizQuery(businessId)}`),
  },
  departments: {
    list: (businessId?: string) => request<{ departments: Department[] }>('GET', `/business/departments${bizQuery(businessId)}`),
    create: (name: string, directorate_id: string, businessId?: string) =>
      request<{ department: Department }>('POST', `/business/departments${bizQuery(businessId)}`, { name, directorate_id }),
    update: (id: string, data: Partial<Department>, businessId?: string) =>
      request<{ department: Department }>('PUT', `/business/departments/${id}${bizQuery(businessId)}`, data),
    remove: (id: string, businessId?: string) =>
      request<{ message: string }>('DELETE', `/business/departments/${id}${bizQuery(businessId)}`),
  },
  budgets: {
    list: (year: number, businessId?: string) =>
      request<{ year: number; budgets: Budget[] }>('GET', `/business/budgets?year=${year}${businessId ? `&business_id=${businessId}` : ''}`),
    create: (data: { department_id: string; fiscal_year: number; amount_allocated: number }, businessId?: string) =>
      request<{ budget: Budget }>('POST', `/business/budgets${bizQuery(businessId)}`, data),
    update: (id: string, amount_allocated: number, businessId?: string) =>
      request<{ budget: Budget }>('PUT', `/business/budgets/${id}${bizQuery(businessId)}`, { amount_allocated }),
    remove: (id: string, businessId?: string) =>
      request<{ message: string }>('DELETE', `/business/budgets/${id}${bizQuery(businessId)}`),
  },
  workflows: {
    list: (businessId?: string) =>
      request<{ workflows: Workflow[]; steps: WorkflowStep[] }>('GET', `/business/workflows${bizQuery(businessId)}`),
    create: (name: string, businessId?: string) =>
      request<{ workflow: Workflow }>('POST', `/business/workflows${bizQuery(businessId)}`, { name }),
    update: (id: string, data: { name?: string; is_default?: boolean }, businessId?: string) =>
      request<{ workflow: Workflow }>('PUT', `/business/workflows/${id}${bizQuery(businessId)}`, data),
    remove: (id: string, businessId?: string) =>
      request<{ message: string }>('DELETE', `/business/workflows/${id}${bizQuery(businessId)}`),
    addStep: (workflowId: string, data: { step_type: StepType; specific_user_id?: string }, businessId?: string) =>
      request<{ step: WorkflowStep }>('POST', `/business/workflows/${workflowId}/steps${bizQuery(businessId)}`, data),
    removeStep: (workflowId: string, stepId: string, businessId?: string) =>
      request<{ message: string }>('DELETE', `/business/workflows/${workflowId}/steps/${stepId}${bizQuery(businessId)}`),
    moveStep: (workflowId: string, stepId: string, direction: 'up' | 'down', businessId?: string) =>
      request<{ message: string }>('POST', `/business/workflows/${workflowId}/steps/${stepId}/move${bizQuery(businessId)}`, { direction }),
  },
  financeApprovers: {
    list: (businessId?: string) =>
      request<{ financeApprovers: FinanceApprover[] }>('GET', `/business/finance-approvers${bizQuery(businessId)}`),
    add: (user_id: string, businessId?: string) =>
      request<{ message: string }>('POST', `/business/finance-approvers${bizQuery(businessId)}`, { user_id }),
    remove: (userId: string, businessId?: string) =>
      request<{ message: string }>('DELETE', `/business/finance-approvers/${userId}${bizQuery(businessId)}`),
  },
  getDelegate: (businessId?: string) => request<{ business: Business }>('GET', `/business/delegate${bizQuery(businessId)}`),
  setDelegate: (user_id: string | null, businessId?: string) =>
    request<{ business: Business }>('PUT', `/business/delegate${bizQuery(businessId)}`, { user_id }),
};

// Expenses
export const expenses = {
  mine: () => request<{ expenses: Expense[] }>('GET', '/expenses/mine'),
  approvals: () => request<{ expenses: Expense[] }>('GET', '/expenses/approvals'),
  financeQueue: (businessId?: string) =>
    request<{ expenses: Expense[] }>('GET', `/expenses/finance-queue${bizQuery(businessId)}`),
  get: (id: string) => request<{ expense: Expense; events: ExpenseEvent[]; canActAsApprover: boolean }>('GET', `/expenses/${id}`),
  create: (data: { department_id?: string; amount: number; description?: string; expense_date: string; purchase_request_id?: string }) =>
    request<{ expense: Expense }>('POST', '/expenses', data),
  uploadReceipt: async (id: string, file: File) => {
    const res = await fetch(`${BASE}/expenses/${id}/receipt?filename=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any).error || 'Upload failed');
    return data as { message: string; receipt_r2_key: string };
  },
  receiptUrl: (id: string) => `${BASE}/expenses/${id}/receipt`,
  approve: (id: string, comment?: string) =>
    request<{ message: string }>('POST', `/expenses/${id}/approve`, { comment }),
  reject: (id: string, comment: string) =>
    request<{ message: string }>('POST', `/expenses/${id}/reject`, { comment }),
  financeApprove: (id: string, comment?: string) =>
    request<{ message: string }>('POST', `/expenses/${id}/finance-approve`, { comment }),
  financeReject: (id: string, comment: string) =>
    request<{ message: string }>('POST', `/expenses/${id}/finance-reject`, { comment }),
  markPaid: (id: string) => request<{ message: string }>('POST', `/expenses/${id}/mark-paid`),
};

export interface PurchaseRequestDetail {
  purchaseRequest: PurchaseRequest;
  steps: PurchaseRequestStep[];
  events: PurchaseRequestEvent[];
  approvers: { id: string; full_name: string | null; email: string }[];
  convertedExpenseId: string | null;
  canActAsApprover: boolean;
}

// Purchase Requests
export const purchaseRequests = {
  mine: () => request<{ purchaseRequests: PurchaseRequest[] }>('GET', '/purchase-requests/mine'),
  approvals: () => request<{ purchaseRequests: PurchaseRequest[] }>('GET', '/purchase-requests/approvals'),
  get: (id: string) => request<PurchaseRequestDetail>('GET', `/purchase-requests/${id}`),
  create: (data: { department_id?: string; item_description: string; estimated_amount: number; justification?: string; needed_by_date?: string }) =>
    request<{ purchaseRequest: PurchaseRequest }>('POST', '/purchase-requests', data),
  approve: (id: string, comment?: string) =>
    request<{ message: string; purchaseRequest: PurchaseRequest }>('POST', `/purchase-requests/${id}/approve`, { comment }),
  reject: (id: string, comment: string) =>
    request<{ message: string; purchaseRequest: PurchaseRequest }>('POST', `/purchase-requests/${id}/reject`, { comment }),
  cancel: (id: string) => request<{ message: string }>('POST', `/purchase-requests/${id}/cancel`),
};

// Reports
export const reports = {
  departments: (year: number, businessId?: string) =>
    request<{ year: number; departments: DepartmentReportRow[] }>('GET', `/reports/departments?year=${year}${businessId ? `&business_id=${businessId}` : ''}`),
  directorates: (year: number, businessId?: string) =>
    request<{ year: number; directorates: DirectorateReportRow[] }>('GET', `/reports/directorates?year=${year}${businessId ? `&business_id=${businessId}` : ''}`),
  expenses: (filters: { status?: string; department_id?: string; year?: number; from?: string; to?: string }, businessId?: string) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.department_id) params.set('department_id', filters.department_id);
    if (filters.year) params.set('year', String(filters.year));
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (businessId) params.set('business_id', businessId);
    return request<{ expenses: ExpenseReportRow[] }>('GET', `/reports/expenses?${params.toString()}`);
  },
};
